import { Alert, Box, Button, Typography } from '@mui/material';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors below this boundary so a single component crash
 * doesn't blank the whole app. Wrap the AppShell or individual route shells.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <Box sx={{ p: 4, maxWidth: 720, mx: 'auto' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6">Something went wrong.</Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {this.state.error.message}
            </Typography>
          </Alert>
          <Button variant="contained" onClick={this.reset}>
            Try again
          </Button>
        </Box>
      );
    }
    return this.props.children;
  }
}
